import { useCallback, useEffect, useRef, useState } from 'react';
import { Delete, Lock } from 'lucide-react';
import { useAppDispatch } from '../store/hooks';
import { logoutUser } from '../store/slices/authSlice';
import { resetGame } from '../store/slices/gameSlice';
import { useTranslation } from '../i18n';
import { vibrate } from '../gameplay/feedback';
import { getPinLength, verifyPin, PIN_MIN_LENGTH } from '../security/pinLock';

export interface LockScreenProps {
  /** Called after the correct PIN (and after a lockout cleared the PIN). */
  onUnlock: () => void;
}

const KEY =
  'flex h-16 w-16 items-center justify-center rounded-full bg-white/10 text-2xl font-medium text-white ' +
  'transition-colors active:bg-white/25 enabled:hover:bg-white/15 disabled:opacity-40 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60';

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

/**
 * P1-S1 — the PIN screen. Deliberately neutral: no logo, no app name, no
 * colour from the brand palette. While it is up the app tree is not rendered
 * at all (see `App.tsx`), so nothing sensitive sits in the DOM behind it.
 *
 * Five wrong attempts in a row erase the PIN and sign the user out.
 */
export default function LockScreen({ onUnlock }: LockScreenProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const length = getPinLength() ?? PIN_MIN_LENGTH;
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  const [shakes, setShakes] = useState(0);
  const [lockedOut, setLockedOut] = useState(false);
  const mountedRef = useRef(true);
  const onUnlockRef = useRef(onUnlock);
  onUnlockRef.current = onUnlock;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const press = useCallback(
    (digit: string) => {
      if (busy || lockedOut) return;
      vibrate(8);
      setPin(current => (current.length < length ? current + digit : current));
    },
    [busy, lockedOut, length]
  );

  const backspace = useCallback(() => {
    if (busy || lockedOut) return;
    vibrate(8);
    setPin(current => current.slice(0, -1));
  }, [busy, lockedOut]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (/^\d$/.test(event.key)) {
        press(event.key);
      } else if (event.key === 'Backspace') {
        backspace();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [press, backspace]);

  useEffect(() => {
    if (pin.length !== length || busy) return;
    setBusy(true);
    void verifyPin(pin)
      .then(result => {
        if (result.status === 'locked-out') {
          // Clearing the PIN already unmounted this screen (the lock follows
          // the PIN), so sign out whether or not it is still mounted.
          dispatch(resetGame());
          void dispatch(logoutUser());
          if (mountedRef.current) {
            setLockedOut(true);
            onUnlockRef.current();
          }
          return;
        }
        if (!mountedRef.current) return;
        if (result.status === 'ok') {
          // Stay busy: the keypad must not submit the same PIN again.
          onUnlockRef.current();
          return;
        }
        vibrate([40, 40, 40]);
        setPin('');
        setShakes(count => count + 1);
        setAttemptsLeft(result.attemptsLeft);
        setBusy(false);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setPin('');
        setBusy(false);
      });
  }, [pin, length, busy, dispatch]);

  const message = lockedOut
    ? t('lock.lockedOut')
    : attemptsLeft !== null
      ? t('lock.wrong', { count: attemptsLeft })
      : '';

  return (
    <main
      aria-labelledby="lock-title"
      className="fixed inset-0 z-[90] flex flex-col items-center justify-center gap-6 bg-hc-base px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]"
    >
      <Lock className="h-7 w-7 text-white/50" aria-hidden="true" />
      <h1
        id="lock-title"
        className="font-sans text-lg font-medium text-white/80"
      >
        {t('lock.title')}
      </h1>

      <div
        key={shakes}
        role="img"
        aria-label={t('lock.entered', { count: pin.length, total: length })}
        className={`flex h-4 items-center gap-4 ${shakes > 0 ? 'motion-safe:animate-shake' : ''}`}
      >
        {Array.from({ length }, (_, index) => (
          <span
            key={index}
            aria-hidden="true"
            className={`h-3.5 w-3.5 rounded-full border border-white/50 ${
              index < pin.length ? 'bg-plum-200' : 'bg-transparent'
            }`}
          />
        ))}
      </div>

      <p
        role="alert"
        className="min-h-[1.25rem] text-center text-sm text-red-300"
      >
        {message}
      </p>

      <div
        dir="ltr"
        role="group"
        aria-label={t('lock.keypad')}
        className="grid grid-cols-3 gap-x-6 gap-y-4"
      >
        {DIGITS.map(digit => (
          <button
            key={digit}
            type="button"
            className={KEY}
            disabled={busy || lockedOut}
            onClick={() => press(digit)}
          >
            {digit}
          </button>
        ))}
        <span aria-hidden="true" />
        <button
          type="button"
          className={KEY}
          disabled={busy || lockedOut}
          onClick={() => press('0')}
        >
          0
        </button>
        <button
          type="button"
          className={`${KEY} bg-transparent`}
          disabled={busy || lockedOut || pin.length === 0}
          onClick={backspace}
          aria-label={t('lock.backspace')}
        >
          <Delete className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </main>
  );
}
